import { NavLink, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Home as HomeIcon, MessageSquare, Trophy, Shield, Users, Database as DBIcon, Map as MapIcon, BookOpen,
  GitCompare, Copy, Radio, Film, Camera, FileText, Calendar, Target, Wrench, Zap, ListOrdered, UserCheck, Medal,
  Star, Gift, Flag, X, Server, Coins, Heart, ShieldAlert, Ban, Sprout, Crown,
} from "lucide-react";
import { toast } from "sonner";
import OnlineNow from "./OnlineNow";
import InstallPWA from "./InstallPWA";
import { StoreNavItem } from "./StoreMegaMenu";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { SERVER_ID } from "./JoinModal";

export const DISCORD_INVITE = "https://discord.gg/ocedogs";
export const DONATE_URL = "https://www.patreon.com/cw/OCEDOGS";

export const NAV_GROUPS = [
  { label: "OPERATIONS", items: [
    { to: "/", label: "Home", icon: HomeIcon },
    { to: "/map", label: "Interactive Maps", icon: MapIcon, hot: true },
    { to: "/season", label: "Season Pass", icon: Flag, hot: true },
    { to: "/loot", label: "Loot Drops", icon: Gift },
    { to: "/live", label: "Live Server", icon: Radio },
    { to: "/seeding", label: "Seed Levels", icon: Sprout, hot: true },
    { to: "/community", label: "Our Servers", icon: Server, hot: true },
    { to: "/matches", label: "Match Feed", icon: Zap },
  ] },
  { label: "COMMUNITY", items: [
    { to: "/forum", label: "Forum", icon: MessageSquare },
    { to: "/blog", label: "Blog", icon: FileText },
    { to: "/store", label: "Store", icon: HomeIcon, store: true },
    { to: "/leaderboard", label: "Leaderboard", icon: Trophy },
    { to: "/clans", label: "Clans", icon: Shield },
    { to: "/team", label: "Staff Team", icon: UserCheck },
    { to: "/founders-wall", label: "Founders Wall", icon: Crown },
    { to: "/tournaments", label: "Tournaments", icon: Trophy, hot: true },
    { to: "/ladders", label: "Ladders", icon: ListOrdered, hot: true },
    { to: "/friends", label: "Friends & DMs", icon: UserCheck },
    { to: "/events", label: "Events", icon: Calendar },
    { to: "/challenges", label: "Challenges", icon: Target },
    { to: "/support", label: "Report a Player", icon: ShieldAlert },
    { to: "/bans", label: "Ban List", icon: Ban },
  ] },
  { label: "CONTENT", items: [
    { to: "/guides", label: "Guides", icon: BookOpen },
    { to: "/clips", label: "Clips", icon: Film },
    { to: "/screenshots", label: "Screenshots", icon: Camera },
    { to: "/patch-notes", label: "Patch Notes", icon: FileText },
  ] },
  { label: "DATA", items: [
    { to: "/database", label: "Database", icon: DBIcon },
    { to: "/compare", label: "Compare", icon: GitCompare },
    { to: "/economy", label: "Economy", icon: Coins, hot: true },
    { to: "/ttk", label: "TTK Calc", icon: Zap },
    { to: "/loadouts", label: "Loadouts", icon: Wrench },
    { to: "/tierlist", label: "Tier List", icon: ListOrdered },
    { to: "/roles", label: "Role Levels", icon: Medal },
    { to: "/market", label: "Marketplace", icon: Star },
  ] },
];

const NavItem = ({ item, collapsed, onNavigate, live }) => {
  let badge = null;
  if (!collapsed && live) {
    const on = live.status === "online";
    const rec = live.status === "reconnecting";
    const c = on ? "#2ECC71" : rec ? "#FFB703" : "#E4443A";
    const label = (on || rec) ? `${live.players ?? 0}` : "OFF";
    badge = (
      <span className="ml-auto inline-flex items-center gap-1 text-[9px] font-mono font-bold px-1.5 py-0.5 tactical-frame-sm" data-testid="nav-live-count" style={{ background: `${c}1F`, color: c, boxShadow: on ? `0 0 8px -2px ${c}` : "none" }} title={on ? `${live.players ?? 0}/${live.max_players ?? 100} operators online` : "Server offline"}>
        <span className={`w-1.5 h-1.5 rounded-full ${on ? "animate-pulse" : ""}`} style={{ background: c }} />
        {label}
      </span>
    );
  }
  return (
    <NavLink
      to={item.to}
      end={item.to === "/"}
      onClick={onNavigate}
      data-testid={`nav-link-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className={({ isActive }) => `nav-item group flex items-center gap-3 h-10 ${collapsed ? "justify-center px-0" : "px-3"} font-stencil font-bold uppercase text-[13px] tracking-wide ${isActive ? "active text-[#D4A94A] bg-[#D4A94A]/10" : "text-[#94A3B8] hover:text-[#F1F5F9] hover:bg-[#13151B]"}`}
    >
      <item.icon className="w-[18px] h-[18px] shrink-0" strokeWidth={2.1} />
      {!collapsed && <span className="truncate">{item.label}</span>}
      {badge}
      {!collapsed && !live && item.hot && <span className="ml-auto text-[8px] font-mono font-bold tracking-widest bg-[#D4A94A] text-black px-1 py-px">HOT</span>}
      {collapsed && <span className="nav-tip glass px-2.5 py-1.5 text-xs font-stencil font-bold uppercase text-[#F1F5F9]">{item.label}</span>}
    </NavLink>
  );
};

export const SidebarBody = ({ collapsed, onNavigate }) => {
  const { user } = useAuth();
  const [srv, setSrv] = useState(null);
  useEffect(() => {
    let alive = true;
    const poll = () => api.get("/server/status").then((r) => { if (alive) setSrv(r.data); }).catch(() => { if (alive) setSrv({ status: "offline" }); });
    poll();
    const t = setInterval(poll, 30000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  const srvStatus = srv?.status;
  const online = srvStatus === "online";
  const reconnecting = srvStatus === "reconnecting";
  const dotColor = online ? "#2ECC71" : reconnecting ? "#FFB703" : "#E4443A";
  const [community, setCommunity] = useState(null);
  useEffect(() => {
    let alive = true;
    const poll = () => api.get("/community/servers").then((r) => {
      if (!alive) return;
      const list = r.data.servers || [];
      setCommunity(list.length ? {
        status: list.some((s) => s.online) ? "online" : "offline",
        players: list.reduce((n, s) => n + (s.players || 0), 0),
        max_players: list.reduce((n, s) => n + (s.max_players || 0), 0),
      } : null);
    }).catch(() => {});
    poll();
    const t = setInterval(poll, 45000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  const [rconAccess, setRconAccess] = useState(false);
  useEffect(() => {
    if (!user) { setRconAccess(false); return; }
    api.get("/rcon/me").then((r) => setRconAccess(!!r.data.has_access)).catch(() => setRconAccess(false));
  }, [user]);
  const [storeMenu, setStoreMenu] = useState(null);
  useEffect(() => {
    api.get("/store/megamenu").then((r) => setStoreMenu(r.data?.enabled ? r.data : null)).catch(() => setStoreMenu(null));
  }, [user]);
  const copyIp = () => { navigator.clipboard.writeText(SERVER_ID); toast.success("Join ID copied"); };
  return (
    <div className="h-full flex flex-col bg-[#0E0F12] border-r border-[#D4A94A]/10">
      <nav className={`flex-1 overflow-y-auto overflow-x-hidden py-3 ${collapsed ? "px-2" : "px-3"}`}>
        {!user?.is_patron && (
          <Link to="/patreon" onClick={onNavigate} data-testid="sidebar-vip-link" title="Subscribe to VIP"
            className={`mb-3 flex items-center gap-3 h-10 ${collapsed ? "justify-center px-0" : "px-3"} bg-[#F96854] hover:bg-[#fa7d6c] text-white tactical-frame-sm font-stencil font-black uppercase text-[13px] transition-colors shadow-[0_0_14px_rgba(249,104,84,0.35)]`}>
            <Crown className="w-[18px] h-[18px] shrink-0" strokeWidth={2.2} />
            {!collapsed && <span className="truncate">Subscribe to VIP</span>}
          </Link>
        )}
        {NAV_GROUPS.map((g, gi) => (
          <div key={g.label} className={gi > 0 ? "mt-3" : ""}>
            {collapsed ? <div className="mx-2 my-2 border-t border-[#2A2E3D]" /> : <div className="overline text-[9px] px-3 mb-1 opacity-80">// {g.label}</div>}
            <div className="flex flex-col gap-0.5">{g.items.map((it) => (
              it.store
                ? (storeMenu ? <StoreNavItem key={it.to} collapsed={collapsed} onNavigate={onNavigate} data={storeMenu} /> : null)
                : <NavItem key={it.to} item={it} collapsed={collapsed} onNavigate={onNavigate} live={it.to === "/live" ? srv : it.to === "/community" ? community : null} />
            ))}</div>
          </div>
        ))}
        {(user?.role === "admin" || user?.role === "moderator") && (
          <Link to="/admin" onClick={onNavigate} data-testid="sidebar-admin-link" className={`mt-3 flex items-center gap-3 h-10 ${collapsed ? "justify-center" : "px-3"} text-[#D4A94A] border border-[#D4A94A]/40 hover:bg-[#D4A94A] hover:text-black tactical-frame-sm font-stencil font-bold uppercase text-[13px] transition-colors`}>
            <Shield className="w-[18px] h-[18px]" /> {!collapsed && (user.role === "admin" ? "Admin" : "Mod Tools")}
          </Link>
        )}
        {rconAccess && (
          <Link to="/rcon" onClick={onNavigate} data-testid="sidebar-rcon-link" className={`mt-2 flex items-center gap-3 h-10 ${collapsed ? "justify-center" : "px-3"} text-[#2EC4B6] border border-[#2EC4B6]/40 hover:bg-[#2EC4B6] hover:text-black tactical-frame-sm font-stencil font-bold uppercase text-[13px] transition-colors`}>
            <Radio className="w-[18px] h-[18px]" /> {!collapsed && "RCON Panel"}
          </Link>
        )}
      </nav>
      <div className={`shrink-0 border-t border-[#2A2E3D] ${collapsed ? "p-2" : "p-3"} space-y-2`}>
        {!collapsed && <InstallPWA className="w-full justify-center" />}
        <a
          href={DISCORD_INVITE}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="sidebar-discord-join"
          title="Join our Discord"
          className={`w-full flex items-center gap-2 bg-[#5865F2] hover:bg-[#6774f5] text-white tactical-frame-sm transition-colors ${collapsed ? "justify-center p-2" : "px-3 py-2"}`}
        >
          <MessageSquare className="w-[18px] h-[18px] shrink-0" strokeWidth={2.2} />
          {!collapsed && <span className="font-stencil font-bold uppercase text-xs">Join Discord</span>}
        </a>
        <a
          href={DONATE_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="sidebar-donate"
          title="Support the server — donate to offset hosting costs"
          className={`w-full flex items-center gap-2 border border-[#D4A94A]/50 text-[#D4A94A] hover:bg-[#D4A94A] hover:text-black tactical-frame-sm transition-colors ${collapsed ? "justify-center p-2" : "px-3 py-2"}`}
        >
          <Heart className="w-[18px] h-[18px] shrink-0" strokeWidth={2.2} />
          {!collapsed && <span className="font-stencil font-bold uppercase text-xs">Support the Server</span>}
        </a>
        <button onClick={copyIp} data-testid="sidebar-copy-ip" className={`w-full text-left ${collapsed ? "p-2 flex justify-center" : "px-3 py-2"} bg-[#13151B] border border-[#2A2E3D] hover:border-[#D4A94A] tactical-frame-sm transition-colors group`} title="Copy Join ID">
          {collapsed ? <span className="w-2 h-2 rounded-full inline-block" style={{ background: dotColor }} /> : (
            <>
              <div className="flex items-center gap-2 mb-0.5"><span className="w-2 h-2 rounded-full" style={{ background: dotColor }} /><span className="font-stencil font-bold uppercase text-xs">Server // {online ? "Online" : reconnecting ? "Reconnecting…" : "Offline"}</span></div>
              <div className="flex items-center justify-between gap-2 font-mono text-[10px] text-[#94A3B8]"><span className="truncate">Join ID: {SERVER_ID}</span><Copy className="w-3 h-3 shrink-0 group-hover:text-[#D4A94A]" /></div>
            </>
          )}
        </button>
        <OnlineNow collapsed={collapsed} />
      </div>
    </div>
  );
};

const Sidebar = ({ collapsed, mobileOpen, onMobileClose }) => (
  <>
    <motion.aside
      data-testid="left-sidebar"
      data-collapsed={collapsed ? "true" : "false"}
      initial={false}
      animate={{ width: collapsed ? 64 : 240 }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
      className="hidden lg:block fixed top-16 bottom-0 left-0 z-40"
    >
      <SidebarBody collapsed={collapsed} />
    </motion.aside>
    {mobileOpen && (
      <>
        <div className="lg:hidden fixed inset-0 z-50 bg-black/70 backdrop-blur-sm" onClick={onMobileClose} />
        <aside data-testid="mobile-sidebar" className="lg:hidden fixed inset-y-0 left-0 w-72 z-50">
          <button onClick={onMobileClose} className="absolute top-3 right-3 z-10 text-[#94A3B8]" aria-label="Close"><X className="w-5 h-5" /></button>
          <div className="h-16 flex items-center gap-2 px-5 border-b border-[#2A2E3D] bg-[#0E0F12]"><img src="/oce-dogs-logo.webp" alt="OCE DOGS" className="h-9 w-auto" /><span className="font-heading font-black uppercase">OCE Dogs</span></div>
          <div className="h-[calc(100%-4rem)]"><SidebarBody collapsed={false} onNavigate={onMobileClose} /></div>
        </aside>
      </>
    )}
  </>
);

export default Sidebar;
